/**
 * Configuration Management System
 * Handles global app configuration with validation and type safety
 */
import { useEffect, useState } from 'react';
import { z } from 'zod';

// ============================================================================
// Configuration Schema
// ============================================================================

export const ThemeConfigSchema = z.object({
  activeTheme: z.string(),
  mode: z.enum(['light', 'dark']).default('light'),
  availableThemes: z.array(z.string()),
});

export const I18nConfigSchema = z.object({
  defaultLanguage: z.string(),
  availableLanguages: z.array(z.string()),
  fallbackLanguage: z.string().optional(),
});

export const FeaturesConfigSchema = z.object({
  authentication: z.boolean().default(true),
  cart: z.boolean().default(true),
  checkout: z.boolean().default(true),
  wishlist: z.boolean().default(false),
  reviews: z.boolean().default(false),
  analytics: z.boolean().default(false),
});

export const ApiConfigSchema = z.object({
  baseUrl: z.string().default('/'),
  timeout: z.number().default(30000),
  retryAttempts: z.number().default(3),
  version: z.string().default('v2'),
  cache: z
    .object({
      enabled: z.boolean().default(false),
      defaultTTL: z.number().default(60000),
      maxSize: z.number().default(50),
    })
    .optional(),
  allowedResources: z.array(z.string()).optional(),
  backendUrl: z.string().optional(), // Server-side only, never exposed to client
});

export const ShopConfigSchema = z
  .object({
    // Remote shop settings - these will be merged from API
    'branding.logo': z.any().optional(),
    'branding.favicon': z.any().optional(),
    'branding.banners': z.any().optional(),
    'site.title': z.string().optional(),
    'site.description': z.string().optional(),
    'site.tagline': z.string().optional(),
    'business.locale': z.string().optional(),
    'business.currency': z.string().optional(),
    'business.currencySymbol': z.string().optional(),
    'business.taxRate': z.any().optional(),
    'business.socialLinks': z.any().optional(),
    'contact.phones': z.any().optional(),
    'contact.emails': z.any().optional(),
    'contact.whatsapp': z.string().optional(),
    'ui.flashMessage': z.any().optional(),
    bank_accounts: z.any().optional(),
  })
  .catchall(z.any()); // Allow any other settings from remote

export const PagesConfigSchema = z.object({
  manifest: z.array(
    z.object({
      name: z.string(),
      path: z.string(),
      component: z.string(),
      layout: z.string(),
      metadata: z.record(z.string(), z.any()).optional(),
      requiresAuth: z.boolean().optional(),
    }),
  ),
});

export const AppConfigSchema = z.object({
  app: z.object({
    name: z.string(),
    description: z.string().optional(),
    version: z.string(),
    environment: z.enum(['development', 'staging', 'production']),
  }),
  theme: ThemeConfigSchema,
  i18n: I18nConfigSchema,
  features: FeaturesConfigSchema,
  api: ApiConfigSchema,
  shop: ShopConfigSchema.optional(),
  metadata: z.record(z.string(), z.any()).optional(),
});

export type AppConfig = z.infer<typeof AppConfigSchema>;
export type ThemeConfig = z.infer<typeof ThemeConfigSchema>;
export type I18nConfig = z.infer<typeof I18nConfigSchema>;
export type FeaturesConfig = z.infer<typeof FeaturesConfigSchema>;
export type ApiConfig = z.infer<typeof ApiConfigSchema>;
export type ShopConfig = z.infer<typeof ShopConfigSchema>;
export type PagesConfig = z.infer<typeof PagesConfigSchema>;

// ============================================================================
// Configuration Manager
// ============================================================================

export class ConfigurationManager {
  private static instance: ConfigurationManager;
  private config: AppConfig | null = null;
  private listeners: Set<(config: AppConfig) => void> = new Set();

  private constructor() {}

  static getInstance(): ConfigurationManager {
    if (!ConfigurationManager.instance) {
      ConfigurationManager.instance = new ConfigurationManager();
    }
    return ConfigurationManager.instance;
  }

  /**
   * Initialize configuration
   */
  async initialize(configData: unknown): Promise<void> {
    try {
      this.config = AppConfigSchema.parse(configData);
      this.notifyListeners();
    } catch (error) {
      if (error instanceof z.ZodError) {
        throw new ConfigurationError('Invalid configuration', error.issues);
      }
      throw error;
    }
  }

  /**
   * Get current configuration
   */
  getConfig(): AppConfig {
    if (!this.config) {
      throw new Error('Configuration not initialized');
    }
    return this.config;
  }

  /**
   * Get a specific configuration section
   */
  getSection<K extends keyof AppConfig>(section: K): AppConfig[K] {
    return this.getConfig()[section];
  }

  /**
   * Update configuration (partial)
   */
  async updateConfig(updates: Partial<AppConfig>): Promise<void> {
    if (!this.config) {
      throw new Error('Configuration not initialized');
    }

    const merged = { ...this.config, ...updates };

    try {
      this.config = AppConfigSchema.parse(merged);
      this.notifyListeners();
    } catch (error) {
      if (error instanceof z.ZodError) {
        throw new ConfigurationError('Invalid configuration update', error.issues);
      }
      throw error;
    }
  }

  /**
   * Subscribe to configuration changes
   */
  subscribe(listener: (config: AppConfig) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Check if a feature is enabled
   */
  isFeatureEnabled(feature: keyof FeaturesConfig): boolean {
    return this.getSection('features')[feature] ?? false;
  }

  /**
   * Get metadata value
   */
  getMetadata<T = any>(key: string, defaultValue?: T): T | undefined {
    const metadata = this.getSection('metadata');
    if (!metadata) return defaultValue;
    return (metadata[key] as T) ?? defaultValue;
  }

  private notifyListeners(): void {
    if (!this.config) return;

    this.listeners.forEach((listener) => {
      try {
        listener(this.config!);
      } catch (error) {
        console.error('Error in config listener:', error);
      }
    });
  }
}

export class ConfigurationError extends Error {
  constructor(
    message: string,
    public validationErrors?: z.ZodIssue[],
  ) {
    super(message);
    this.name = 'ConfigurationError';
  }
}

export const getConfigManager = () => ConfigurationManager.getInstance();

// ============================================================================
// Configuration Loaders
// ============================================================================

/**
 * Load configuration from API
 */
export async function loadConfigFromApi(url: string = '/api/config'): Promise<AppConfig> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to load configuration: ${response.statusText}`);
    }
    const data = await response.json();
    return AppConfigSchema.parse(data);
  } catch (error) {
    throw new Error(`Configuration loading failed: ${error}`);
  }
}

/**
 * Merge multiple configuration sources
 */
export function mergeConfigs(...configs: Partial<AppConfig>[]): AppConfig {
  const merged = configs.reduce((acc, config) => {
    return {
      ...acc,
      ...config,
      // Deep merge for nested objects
      theme: { ...(acc.theme || {}), ...(config.theme || {}) },
      i18n: { ...(acc.i18n || {}), ...(config.i18n || {}) },
      features: { ...(acc.features || {}), ...(config.features || {}) },
      api: { ...(acc.api || {}), ...(config.api || {}) },
      metadata: { ...(acc.metadata || {}), ...(config.metadata || {}) },
    };
  }, {} as any);

  return AppConfigSchema.parse(merged);
}

/**
 * React hook to use configuration
 */
export function useConfig(): AppConfig | null {
  const [config, setConfig] = useState<AppConfig | null>(null);

  useEffect(() => {
    const manager = getConfigManager();
    try {
      const currentConfig = manager.getConfig();
      setConfig(currentConfig);
    } catch (_) {
      // Config not initialized yet
    }

    const unsubscribe = manager.subscribe(setConfig);
    return unsubscribe;
  }, []);

  return config;
}
